/**
 * Strip auth credentials out of a URL before it is written to a log.
 *
 * WHY: `errorReporting.js` sends `url: window.location.href` to the
 * `log-client-error` edge function, which persists it to `public.client_error_log`
 * — a durable table. Supabase auth flows put LIVE CREDENTIALS in the URL:
 *
 *   • password reset / magic link → `?token_hash=…&type=recovery`  (PKCE-style,
 *     the pattern CrewVex uses — see ResetPassword.js "Phase 2b")
 *   • older implicit flows        → `#access_token=…&refresh_token=…`
 *
 * So a JavaScript error thrown while a user sits on the reset-password screen
 * would write a working recovery token into a database row. Single-use and
 * short-lived, but a credential in a log is a credential in a log.
 *
 * WHAT THIS DOES NOT DO: it does not blanket-strip the query string. Real
 * debugging context lives there (`?pool=onDemand`, `?view=history` — the
 * /app/:feature routing params), and throwing that away would make error reports
 * meaningfully worse. Only known credential keys are redacted; everything else
 * survives.
 *
 * Redacts rather than deletes, so a report still shows that a token WAS present
 * (useful when reasoning about which flow the user was in) without leaking it.
 */

/** Auth credential parameter names, lowercased. Redacted in both query and hash. */
export const SENSITIVE_URL_PARAMS = [
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'token_hash',
  'token',
  'code',
  'otp',
  'confirmation_token',
  'recovery_token',
  'email_change_token',
  // RD-SEC-045: the invite-link token (`/accept-invite?invite=<43 chars>`) is a
  // bearer credential until the invite is accepted — never log or persist it.
  'invite',
  // RD-NAV-002: the admin History/Account-Activity/platform-Account-Activity
  // free-text search boxes persist their query into the URL — a customer
  // name, an email fragment, an IP the admin typed while investigating —
  // which is exactly the kind of user-entered PII this file exists to keep
  // out of the durable client_error_log AND (via lastRouteStorage.saveLastRoute)
  // out of localStorage.
  'act_q',
  'acctact_q',
  'pact_q',
];

const REDACTED = '[redacted]';

function scrubParamString(raw) {
  if (!raw) return raw;
  const lead = raw[0] === '?' || raw[0] === '#' ? raw[0] : '';
  const body = lead ? raw.slice(1) : raw;
  if (!body) return raw;

  // A hash is not always key=value (e.g. "#section"). If nothing looks like a
  // pair, leave it untouched rather than mangling a legitimate fragment.
  if (!body.includes('=')) return raw;

  const scrubbed = body
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return pair;
      const key = pair.slice(0, eq);
      return SENSITIVE_URL_PARAMS.includes(key.toLowerCase())
        ? `${key}=${REDACTED}`
        : pair;
    })
    .join('&');

  return `${lead}${scrubbed}`;
}

/**
 * @param {string} href - typically `window.location.href`
 * @returns {string} the same URL with any auth credentials redacted
 */
export function sanitizeUrlForLogging(href) {
  if (typeof href !== 'string' || !href) return '';
  try {
    const u = new URL(href);
    u.search = scrubParamString(u.search);
    u.hash = scrubParamString(u.hash);
    return u.toString();
  } catch (_e) {
    // Not parseable as an absolute URL (relative path, or a bare fragment).
    // Fail CLOSED: if we cannot reliably locate the credential portion, drop
    // everything from the first `?` or `#` rather than logging it verbatim.
    const cut = href.search(/[?#]/);
    return cut === -1 ? href : href.slice(0, cut);
  }
}

export default sanitizeUrlForLogging;
